'use client';

import React, {
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Box,
  Divider,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import DOMPurify from 'dompurify';

const TableQuestion = forwardRef(
  (
    {
      questionData,
      onAnswerChange,
      primaryColor = '#1976d2',
      hasError = false,
      hasOwnBold,
    },
    ref
  ) => {
    const [rows, setRows] = useState([
      {
        id: crypto.randomUUID(),
        data: {},
      },
    ]);

    const localRef = useRef(null);

    useImperativeHandle(ref, () => ({
      validate: () => validateAll(),
      getAnswers: () => {
        // ✅ Extraer headers de las columnas
        const headers = questionData.tableSetting.options.map(
          (column) => column.value
        );

        // ✅ Crear filas con los datos
        const tableRows = rows.map((row) =>
          questionData.tableSetting.options.map(
            (column) => row.data[column.value] || ''
          )
        );

        return {
          id: questionData.questionId,
          nameQuestion: questionData.questionName,
          questionType: 'Tabla',
          selectionValues: {},
          selectionTable: {
            headers: headers,
            rows: tableRows,
          },
          value: '',
          values: {},
        };
      },
      hasErrors: () => validateAll().length > 0,
      scrollIntoView: () => {
        localRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      },
    }));

    const validateAll = () => {
      const errors = [];
      
      rows.forEach((row, rowIndex) => {
        questionData.tableSetting.options.forEach((column) => {
          const cellValue = row.data[column.value];
          
          if (column.required && (!cellValue || cellValue.trim() === '')) {
            errors.push(
              `Fila ${rowIndex + 1}, ${column.value}: Este campo es obligatorio`
            );
          }
          
          if (cellValue && cellValue.trim() !== '') {
            if (column.type === 'number' && isNaN(cellValue)) {
              errors.push(
                `Fila ${rowIndex + 1}, ${column.value}: Debe ser un número`
              );
            }
            
            if (column.type === 'email') {
              const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
              if (!emailRegex.test(cellValue)) {
                errors.push(
                  `Fila ${rowIndex + 1}, ${column.value}: Email inválido`
                );
              }
            }
          }
        });
      });
      
      return errors;
    };

    const handleCellChange = (rowId, columnName, value, columnType) => {
      if (columnType === 'number') {
        if (value !== '' && !/^\d+$/.test(value)) {
          return;
        }
      }

      const updated = rows.map((row) =>
        row.id === rowId
          ? {
              ...row,
              data: {
                ...row.data,
                [columnName]: value,
              },
            }
          : row
      );
      setRows(updated);
      
      if (onAnswerChange) {
        // ✅ Crear headers y rows
        const headers = questionData.tableSetting.options.map(
          (column) => column.value
        );

        const tableRows = updated.map((row) =>
          questionData.tableSetting.options.map(
            (column) => row.data[column.value] || ''
          )
        );

        onAnswerChange({
          id: questionData.questionId,
          nameQuestion: questionData.questionName,
          questionType: 'Tabla',
          selectionValues: {},
          selectionTable: {
            headers: headers,
            rows: tableRows,
          },
          value: '',
          values: {},
        });
      }
    };

    const addRow = () => {
      const newRow = {
        id: crypto.randomUUID(),
        data: {},
      };
      const updated = [...rows, newRow];
      setRows(updated);
      
      if (onAnswerChange) {
        const headers = questionData.tableSetting.options.map(
          (column) => column.value
        );

        const tableRows = updated.map((row) =>
          questionData.tableSetting.options.map(
            (column) => row.data[column.value] || ''
          )
        );

        onAnswerChange({
          id: questionData.questionId,
          nameQuestion: questionData.questionName,
          questionType: 'Tabla',
          selectionValues: {},
          selectionTable: {
            headers: headers,
            rows: tableRows,
          },
          value: '',
          values: {},
        });
      }
    };

    const removeRow = (rowId) => {
      if (rows.length > 1) {
        const updated = rows.filter((row) => row.id !== rowId);
        setRows(updated);
        
        if (onAnswerChange) {
          const headers = questionData.tableSetting.options.map(
            (column) => column.value
          );

          const tableRows = updated.map((row) =>
            questionData.tableSetting.options.map(
              (column) => row.data[column.value] || ''
            )
          );

          onAnswerChange({
            id: questionData.questionId,
            nameQuestion: questionData.questionName,
            questionType: 'Tabla',
            selectionValues: {},
            selectionTable: {
              headers: headers,
              rows: tableRows,
            },
            value: '',
            values: {},
          });
        }
      }
    };

    const getCellError = (rowId, columnName, column) => {
      if (!hasError) return false;
      
      const row = rows.find((r) => r.id === rowId);
      const cellValue = row?.data[columnName];
      
      if (column.required && (!cellValue || cellValue.trim() === '')) {
        return true;
      }
      
      if (cellValue && cellValue.trim() !== '') {
        if (column.type === 'number' && isNaN(cellValue)) {
          return true;
        }
        
        if (column.type === 'email') {
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailRegex.test(cellValue)) {
            return true;
          }
        }
      }
      
      return false;
    };

    const getPlaceholder = (column) => {
      switch (column.type) {
        case 'text':
          return 'Escribe aquí...';
        case 'number':
          return 'Ej: 123';
        case 'email':
          return 'ejemplo@correo.com';
        default:
          return 'Escribe aquí...';
      }
    };

    const getInputType = (column) => {
      return 'text';
    };

    return (
      <Box ref={localRef}>
        <Typography
          variant="h6"
          sx={{
            fontSize: '1.05rem',
            fontWeight: hasOwnBold(questionData.questionName) ? 'normal' : 'bold',
            mb: 1,
            color: hasError ? '#d32f2f' : '#333',
          }}
        >
            <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(questionData.questionName) }} />
        </Typography>

        {questionData.description && (
          <Typography
            variant="body2"
            sx={{
              display: 'block',
              mb: 2,
              color: hasError ? '#d32f2f' : '#666',
              lineHeight: 1.5,
            }}
          >
                <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(questionData.description) }} />
          </Typography>
        )}

        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            borderRadius: '8px',
            border: hasError ? '1px solid #d32f2f' : '1px solid #e0e0e0',
            mb: 2,
            maxWidth: '100%',
            overflowX: 'auto',
          }}
        >
          <Table size="small" sx={{ minWidth: 650 }}>
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    bgcolor: primaryColor,
                    color: 'white',
                    fontWeight: 600,
                    width: '50px',
                    textAlign: 'center',
                    position: 'sticky',
                    left: 0,
                    zIndex: 2,
                    borderRight: '1px solid rgba(255,255,255,0.2)',
                  }}
                >
                  #
                </TableCell>
                {questionData.tableSetting.options.map((column, index) => (
                  <TableCell
                    key={index}
                    sx={{
                      bgcolor: primaryColor,
                      color: 'white',
                      fontWeight: 600,
                      minWidth: '160px',
                      borderRight: index < questionData.tableSetting.options.length - 1 
                        ? '1px solid rgba(255,255,255,0.2)' 
                        : 'none',
                    }}
                  >
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                        {column.value}
                        {column.required && (
                          <span style={{ color: '#ffeb3b', fontSize: '1.1rem' }}>*</span>
                        )}
                      </Box>
                      <Box
                        sx={{
                          display: 'inline-block',
                          bgcolor: 'rgba(255, 255, 255, 0.25)',
                          color: 'white',
                          px: 1,
                          py: 0.25,
                          borderRadius: '4px',
                          fontSize: '0.7rem',
                          fontWeight: 500,
                        }}
                      >
                        {column.type === 'text' && 'Texto'}
                        {column.type === 'number' && 'Número'}
                        {column.type === 'email' && 'Email'}
                      </Box>
                    </Box>
                  </TableCell>
                ))}
                <TableCell
                  sx={{
                    bgcolor: primaryColor,
                    color: 'white',
                    width: '80px',
                    textAlign: 'center',
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    position: 'sticky',
                    right: 0,
                    zIndex: 2,
                    borderLeft: '1px solid rgba(255,255,255,0.2)',
                  }}
                >
                  Acciones
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, rowIndex) => (
                <TableRow
                  key={row.id}
                  sx={{
                    '&:hover': {
                      bgcolor: '#fafafa',
                    },
                    '&:last-child td': {
                      borderBottom: 0,
                    },
                  }}
                >
                  <TableCell
                    sx={{
                      bgcolor: '#f5f5f5',
                      textAlign: 'center',
                      fontWeight: 600,
                      color: '#666',
                      position: 'sticky',
                      left: 0,
                      zIndex: 1,
                    }}
                  >
                    {rowIndex + 1}
                  </TableCell>
                  {questionData.tableSetting.options.map((column, colIndex) => {
                    const hasErrorCell = getCellError(row.id, column.value, column);
                    return (
                      <TableCell key={colIndex} sx={{ p: 1 }}>
                        <TextField
                          variant="outlined"
                          placeholder={getPlaceholder(column)}
                          type={getInputType(column)}
                          value={row.data[column.value] || ''}
                          onChange={(e) =>
                            handleCellChange(row.id, column.value, e.target.value, column.type)
                          }
                          fullWidth
                          size="small"
                          error={hasErrorCell}
                          helperText={
                            hasErrorCell
                              ? column.required && (!row.data[column.value] || row.data[column.value].trim() === '')
                                ? 'Obligatorio'
                                : column.type === 'number'
                                ? 'Debe ser número'
                                : 'Email inválido'
                              : ''
                          }
                          inputProps={{
                            ...(column.type === 'number' && {
                              inputMode: 'numeric',
                              pattern: '[0-9]*',
                            }),
                          }}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              bgcolor: '#fff',
                              '& fieldset': {
                                borderColor: '#e0e0e0',
                              },
                              '&:hover fieldset': {
                                borderColor: primaryColor,
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: primaryColor,
                              },
                            },
                          }}
                        />
                      </TableCell>
                    );
                  })}
                  <TableCell 
                    sx={{ 
                      textAlign: 'center',
                      position: 'sticky',
                      right: 0,
                      bgcolor: '#fff',
                      zIndex: 1,
                    }}
                  >
                    {rows.length > 1 && (
                      <IconButton
                        size="small"
                        onClick={() => removeRow(row.id)}
                        sx={{
                          color: '#d32f2f',
                          '&:hover': {
                            bgcolor: 'rgba(211, 47, 47, 0.08)',
                          },
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              <TableRow>
                <TableCell
                  colSpan={questionData.tableSetting.options.length + 2}
                  sx={{
                    textAlign: 'center',
                    bgcolor: '#fafafa',
                    py: 1.5,
                  }}
                >
                  <IconButton
                    onClick={addRow}
                    size="small"
                    sx={{
                      color: primaryColor,
                      '&:hover': {
                        bgcolor: `${primaryColor}15`,
                      },
                    }}
                  >
                    <AddIcon />
                  </IconButton>
                  <Typography
                    variant="body2"
                    component="span"
                    sx={{ ml: 1, color: primaryColor, fontWeight: 500 }}
                  >
                    Agregar fila
                  </Typography>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        {hasError && (
          <Typography
            variant="body2"
            sx={{
              color: '#d32f2f',
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              mb: 2,
              fontWeight: 500,
            }}
          >
            ⚠️ Por favor completa todos los campos obligatorios correctamente
          </Typography>
        )}

        <Divider sx={{ my: 2 }} />
      </Box>
    );
  }
);

TableQuestion.displayName = 'TableQuestion';

export default TableQuestion;